import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, MenuItem, Pagination, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import UndoIcon from '@mui/icons-material/Undo';
import AddIcon from '@mui/icons-material/Add';
import CodeIcon from '@mui/icons-material/Code';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import {
  confirmarNfeXmlImport,
  devolverNfeImportada,
  downloadNfeImportadaXml,
  getAllProducts,
  getEstabelecimentos,
  getNfeImportada,
  getNfesImportadas,
  getProductById,
  previewNfeXmlImport,
} from '../services/api';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';
import NfeXmlNovoProdutoDialog from './NfeXmlNovoProdutoDialog';
import { getApiErrorMessage } from '../utils/apiError';
import {
  formatBRLFromNumber,
  formatCurrencyInputBR,
  parseBRLToNumber,
} from '../utils/decimalBr';

const money = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** tipo: 0=Varejo, 3=Custo */
const pickPrecosProduto = (produto, estabelecimentoId) => {
  const precos = Array.isArray(produto?.precos) ? produto.precos : [];
  const estab = Number(estabelecimentoId) || 0;
  const filtrados = estab
    ? precos.filter((p) => !p.estabelecimentoId || Number(p.estabelecimentoId) === estab)
    : precos;
  const prefer = (tipo) => {
    const candidatos = filtrados.filter((p) => Number(p.tipo) === tipo);
    if (!candidatos.length) return 0;
    const tab1 = candidatos.find((p) => Number(p.tabelaPrecoId) === 1);
    return Number((tab1 || candidatos[0]).valor) || 0;
  };
  return { custo: prefer(3), varejo: prefer(0) };
};

const variacaoCustoDe = (custoAtual, custoNovo) => {
  const a = Number(custoAtual) || 0;
  const n = Number(custoNovo) || 0;
  if (a <= 0) return 0;
  const diff = n - a;
  if (Math.abs(diff) < 0.0001) return 0;
  return diff > 0 ? 1 : -1;
};

export default function NfeXmlImportPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef(null);
  const [estabs, setEstabs] = useState([]);
  const [estabelecimentoId, setEstabelecimentoId] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [xmlText, setXmlText] = useState('');
  const [mapas, setMapas] = useState({}); // indice -> { id, label }
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [productOptions, setProductOptions] = useState({});
  const [searching, setSearching] = useState({});
  const [importadas, setImportadas] = useState([]);
  const [impPagina, setImpPagina] = useState(1);
  const [impPages, setImpPages] = useState(1);
  const [impLoading, setImpLoading] = useState(false);
  const [devolverDlg, setDevolverDlg] = useState({ open: false, item: null });
  const [devolverSaving, setDevolverSaving] = useState(false);
  const [novoProdutoDlg, setNovoProdutoDlg] = useState({ open: false, item: null });
  const [detalheDlg, setDetalheDlg] = useState({ open: false, loading: false, data: null });
  /** indice → { custoAtual, varejoAtual, precoVarejoStr, variacaoCusto, codigo } */
  const [precosLinha, setPrecosLinha] = useState({});
  const [ultimaEtiquetas, setUltimaEtiquetas] = useState([]);

  const carregarImportadas = async (pagina = 1, estabId = estabelecimentoId) => {
    setImpLoading(true);
    try {
      const { data } = await getNfesImportadas({
        pagina,
        qtde: 10,
        estabelecimentoId: estabId ? Number(estabId) : undefined,
      });
      setImportadas(data.items || []);
      setImpPages(data.totalPages || 1);
      setImpPagina(pagina);
    } catch (e) {
      setError(getApiErrorMessage(e, 'Não foi possível listar as NF-e importadas.'));
    } finally {
      setImpLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getEstabelecimentos();
        const lista = Array.isArray(data) ? data : (data ? [data] : []);
        setEstabs(lista.map((e) => ({
          id: e.id,
          nome: e.nomeFantasia || e.razaoSocial || `Estab. ${e.id}`,
        })));
        if (lista.length === 1) setEstabelecimentoId(String(lista[0].id));
        const estabState = location.state?.estabelecimentoId;
        if (estabState) setEstabelecimentoId(String(estabState));
        await carregarImportadas(1, lista.length === 1 ? String(lista[0].id) : (estabState ? String(estabState) : ''));
      } catch (e) {
        setError(getApiErrorMessage(e, 'Falha ao carregar estabelecimentos.'));
      }
    })();
  }, []);

  const pendentes = useMemo(() => {
    if (!preview?.itens) return 0;
    return preview.itens.filter((i) => !mapas[i.indice]?.id).length;
  }, [preview, mapas]);

  const buscarProdutos = async (indice, termo) => {
    if (!termo || termo.trim().length < 2) return;
    setSearching((s) => ({ ...s, [indice]: true }));
    try {
      const { data } = await getAllProducts({ indice: 1, qtde: 20, termo: termo.trim(), ativo: 'true' });
      const opts = (data.items || []).map((p) => ({
        id: p.id,
        codigo: p.codigo,
        descricao: p.descricao,
        label: `${p.codigo || p.id} · ${p.descricao || ''}`,
      }));
      setProductOptions((prev) => ({ ...prev, [indice]: opts }));
    } catch {
      setProductOptions((prev) => ({ ...prev, [indice]: [] }));
    } finally {
      setSearching((s) => ({ ...s, [indice]: false }));
    }
  };

  const aplicarPrecosLinha = (indice, { custoAtual, varejoAtual, codigo }, custoNovo) => {
    const variacao = variacaoCustoDe(custoAtual, custoNovo);
    setPrecosLinha((prev) => ({
      ...prev,
      [indice]: {
        custoAtual: Number(custoAtual) || 0,
        varejoAtual: Number(varejoAtual) || 0,
        precoVarejoStr: formatBRLFromNumber(Number(varejoAtual) || 0),
        variacaoCusto: variacao,
        codigo: codigo || '',
      },
    }));
  };

  const aoSelecionarProduto = async (indice, value, custoNovo) => {
    setMapas((m) => ({ ...m, [indice]: value }));
    if (!value?.id) {
      setPrecosLinha((prev) => {
        const next = { ...prev };
        delete next[indice];
        return next;
      });
      return;
    }
    try {
      const { data } = await getProductById(value.id, Number(estabelecimentoId) || undefined);
      const { custo, varejo } = pickPrecosProduto(data, estabelecimentoId);
      aplicarPrecosLinha(
        indice,
        { custoAtual: custo, varejoAtual: varejo, codigo: data?.codigo || value.codigo },
        custoNovo,
      );
      if (data?.codigo || data?.descricao) {
        setMapas((m) => ({
          ...m,
          [indice]: {
            ...value,
            codigo: data.codigo || value.codigo,
            descricao: data.descricao || value.descricao,
            label: `${data.codigo || value.id} · ${data.descricao || value.descricao || ''}`,
          },
        }));
      }
    } catch {
      aplicarPrecosLinha(
        indice,
        { custoAtual: 0, varejoAtual: 0, codigo: value.codigo },
        custoNovo,
      );
    }
  };

  const gerarPreview = async (arquivo) => {
    if (!Number(estabelecimentoId)) {
      setError('Selecione o estabelecimento.');
      return;
    }
    if (!arquivo) {
      setError('Selecione o arquivo XML.');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    setPreview(null);
    setPrecosLinha({});
    try {
      const text = await arquivo.text();
      setXmlText(text);
      const { data } = await previewNfeXmlImport(Number(estabelecimentoId), arquivo);
      setPreview(data);
      const initial = {};
      const precosInit = {};
      (data.itens || []).forEach((item) => {
        if (item.produtoIdSugerido) {
          const codigo = item.produtoCodigoSugerido || '';
          const desc = item.produtoDescricaoSugerida || '';
          initial[item.indice] = {
            id: item.produtoIdSugerido,
            codigo,
            descricao: desc,
            label: codigo || desc
              ? `${codigo || item.produtoIdSugerido} · ${desc}`
              : `Produto #${item.produtoIdSugerido}`,
          };
          precosInit[item.indice] = {
            custoAtual: Number(item.custoAtual) || 0,
            varejoAtual: Number(item.varejoAtual) || 0,
            precoVarejoStr: formatBRLFromNumber(Number(item.varejoAtual) || 0),
            variacaoCusto: Number(item.variacaoCusto) || 0,
            codigo,
          };
        }
      });
      setMapas(initial);
      setPrecosLinha(precosInit);
      if (data.jaImportada) {
        setError('Esta NF-e já foi importada neste estabelecimento.');
      }
    } catch (e) {
      setError(getApiErrorMessage(e, 'Não foi possível ler o XML.'));
    } finally {
      setLoading(false);
    }
  };

  const incomingXmlRef = useRef(location.state?.xml || '');
  useEffect(() => {
    const xml = incomingXmlRef.current;
    if (!xml || !Number(estabelecimentoId)) return;
    incomingXmlRef.current = '';
    const arquivo = new File([xml], 'nfe.xml', { type: 'text/xml' });
    setFile(arquivo);
    gerarPreview(arquivo);
  }, [estabelecimentoId]);

  const produtosParaEtiquetas = (itensFonte, mapasFonte, precosFonte) => {
    const lista = [];
    const vistos = new Set();
    (itensFonte || []).forEach((item) => {
      const map = mapasFonte?.[item.indice];
      const id = map?.id || item.produtoId;
      if (!id || vistos.has(id)) return;
      vistos.add(id);
      const pl = precosFonte?.[item.indice];
      lista.push({
        id,
        codigo: map?.codigo || pl?.codigo || item.produtoCodigo || item.produtoCodigoSugerido || '',
        descricao: map?.descricao || item.produtoDescricao || item.produtoDescricaoSugerida || item.descricao || '',
        ean: item.ean || '',
        quantidade: 1,
      });
    });
    return lista;
  };

  const irEtiquetas = (produtos) => {
    if (!produtos?.length) {
      setError('Nenhum produto mapeado para imprimir etiquetas.');
      return;
    }
    navigate('/utilities/labels', { state: { produtos } });
  };

  const confirmar = async () => {
    if (!preview || preview.jaImportada) return;
    if (pendentes > 0) {
      setError(`Ainda faltam mapear ${pendentes} item(ns).`);
      return;
    }
    setConfirming(true);
    setError('');
    try {
      const itensPayload = preview.itens.map((item) => {
        const pl = precosLinha[item.indice];
        const varejoNum = parseBRLToNumber(pl?.precoVarejoStr);
        const varejoAtual = Number(pl?.varejoAtual) || 0;
        const mudouVarejo = varejoNum != null && Math.abs(varejoNum - varejoAtual) >= 0.0001;
        return {
          indice: item.indice,
          produtoId: mapas[item.indice].id,
          atualizarCusto: true,
          ...(mudouVarejo && varejoNum > 0 ? { precoVarejo: varejoNum } : {}),
        };
      });
      const payload = {
        estabelecimentoId: Number(estabelecimentoId),
        xml: xmlText,
        itens: itensPayload,
      };
      const produtosEtq = produtosParaEtiquetas(preview.itens, mapas, precosLinha);
      const { data } = await confirmarNfeXmlImport(payload);
      setPreview(null);
      setFile(null);
      setMapas({});
      setPrecosLinha({});
      setXmlText('');
      if (inputRef.current) inputRef.current.value = '';
      await carregarImportadas(1);
      setUltimaEtiquetas(produtosEtq);
      setSuccess(
        produtosEtq.length
          ? `NF-e importada (#${data.nfeImportadaId}). ${data.movimentosCriados} movimento(s). ${produtosEtq.length} produto(s) prontos para etiqueta.`
          : `NF-e importada (#${data.nfeImportadaId}). ${data.movimentosCriados} movimento(s) de estoque.`,
      );
    } catch (e) {
      setError(getApiErrorMessage(e, 'Falha ao confirmar importação.'));
    } finally {
      setConfirming(false);
    }
  };

  const confirmarDevolucao = async () => {
    const item = devolverDlg.item;
    if (!item) return;
    setDevolverSaving(true);
    setError('');
    try {
      const { data } = await devolverNfeImportada(item.id);
      setDevolverDlg({ open: false, item: null });
      navigate('/fiscal/nfes', {
        state: {
          devolver: {
            vendaId: data.vendaId,
            nfeImportadaId: data.nfeImportadaId,
            chaveReferenciada: data.chaveReferenciada,
            naturezaOperacao: data.naturezaOperacao,
            finNFe: data.finNFe,
          },
        },
      });
    } catch (e) {
      setError(getApiErrorMessage(e, 'Não foi possível preparar a devolução.'));
    } finally {
      setDevolverSaving(false);
    }
  };

  const abrirDetalhe = async (item) => {
    if (!item?.id) return;
    setDetalheDlg({ open: true, loading: true, data: null });
    try {
      const { data } = await getNfeImportada(item.id);
      setDetalheDlg({ open: true, loading: false, data });
    } catch (e) {
      setDetalheDlg({ open: false, loading: false, data: null });
      setError(getApiErrorMessage(e, 'Não foi possível carregar os detalhes da NF-e.'));
    }
  };

  const baixarXmlImportado = async (item) => {
    if (!item?.id) return;
    try {
      const { data } = await downloadNfeImportadaXml(item.id);
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = item.chave ? `NFe${item.chave}.xml` : `nfe-importada-${item.id}.xml`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(getApiErrorMessage(e, 'Não foi possível baixar o XML.'));
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Importar XML de NF-e"
        subtitle="Gera entrada de estoque (Compra) a partir do XML da nota de entrada."
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess('')}
          action={ultimaEtiquetas.length > 0 ? (
            <Button
              color="inherit"
              size="small"
              startIcon={<LocalOfferIcon />}
              onClick={() => irEtiquetas(ultimaEtiquetas)}
            >
              Etiquetas
            </Button>
          ) : undefined}
        >
          {success}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <TextField
            select
            label="Estabelecimento"
            size="small"
            value={estabelecimentoId}
            onChange={(e) => {
              setEstabelecimentoId(e.target.value);
              carregarImportadas(1, e.target.value);
            }}
            sx={{ minWidth: 260 }}
          >
            {estabs.map((e) => (
              <MenuItem key={e.id} value={String(e.id)}>{e.nome}</MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            startIcon={<UploadFileIcon />}
            disabled={loading}
            onClick={() => inputRef.current?.click()}
          >
            {loading ? 'Lendo XML...' : 'Selecionar XML'}
          </Button>
          <input
            ref={inputRef}
            hidden
            type="file"
            accept=".xml,text/xml,application/xml"
            onChange={(e) => {
              const f = e.target.files?.[0] || null;
              setFile(f);
              if (f) gerarPreview(f);
            }}
          />
          {file && (
            <Typography variant="body2" color="text.secondary">
              {file.name}
            </Typography>
          )}
        </Stack>
      </Paper>

      {loading && (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <CircularProgress />
        </Box>
      )}

      {preview && !loading && (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap">
              <Chip label={`Chave ${preview.chave}`} />
              <Chip label={`NF ${preview.numero}/${preview.serie}`} />
              <Chip label={preview.nomeEmitente || preview.cnpjEmitente} />
              <Chip label={money(preview.valorTotal)} color="primary" />
              {preview.fornecedorId && (
                <Chip color="success" label={`Fornecedor #${preview.fornecedorId}`} />
              )}
              {preview.jaImportada && <Chip color="error" label="Já importada" />}
              {pendentes > 0 && <Chip color="warning" label={`${pendentes} sem produto`} />}
            </Stack>
          </Paper>

          <Alert severity="info" sx={{ mb: 2 }}>
            Custo da nota em <strong>vermelho</strong> se aumentou e <strong>verde</strong> se diminuiu
            em relação ao cadastro. Ajuste o varejo na coluna &quot;Venda&quot; antes de confirmar —
            o custo da nota será gravado automaticamente.
          </Alert>

          <ResponsiveTable>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>XML</TableCell>
                  <TableCell>EAN</TableCell>
                  <TableCell>Estoque</TableCell>
                  <TableCell align="right">Custo atual</TableCell>
                  <TableCell align="right">Custo nota</TableCell>
                  <TableCell sx={{ minWidth: 130 }}>Venda</TableCell>
                  <TableCell sx={{ minWidth: 260 }}>Produto local</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.itens.map((item) => {
                  const pl = precosLinha[item.indice];
                  const variacao = pl?.variacaoCusto ?? item.variacaoCusto ?? 0;
                  const custoAtual = pl?.custoAtual ?? item.custoAtual ?? 0;
                  const custoNovo = Number(item.custoNovo) || 0;
                  const custoNotaSx = variacao > 0
                    ? { color: 'error.main', fontWeight: 700, bgcolor: 'error.50' }
                    : variacao < 0
                      ? { color: 'success.main', fontWeight: 700, bgcolor: 'success.50' }
                      : {};
                  const varejoNum = parseBRLToNumber(pl?.precoVarejoStr);
                  const margem = custoNovo > 0 && varejoNum != null && varejoNum > 0
                    ? ((varejoNum - custoNovo) / custoNovo) * 100
                    : null;
                  return (
                    <TableRow key={item.indice}>
                      <TableCell data-label="#">{item.indice}</TableCell>
                      <TableCell data-label="XML">
                        <Typography variant="body2" fontWeight={700}>{item.codigoXml}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.descricao}</Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {money(item.valorTotal)} · {item.quantidade}{item.unidade ? ` ${item.unidade}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell data-label="EAN">{item.ean || '—'}</TableCell>
                      <TableCell data-label="Estoque">
                        {item.fatorConversao > 1
                          ? `${item.quantidadeEstoque} UN (${item.fatorConversao}×)`
                          : (item.quantidadeEstoque ?? item.quantidade)}
                      </TableCell>
                      <TableCell align="right" data-label="Custo atual">
                        {mapas[item.indice] ? money(custoAtual) : '—'}
                      </TableCell>
                      <TableCell align="right" data-label="Custo nota" sx={custoNotaSx}>
                        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                          {variacao > 0 && <TrendingUpIcon fontSize="inherit" color="error" />}
                          {variacao < 0 && <TrendingDownIcon fontSize="inherit" color="success" />}
                          <span>{money(custoNovo)}</span>
                        </Stack>
                      </TableCell>
                      <TableCell data-label="Venda">
                        <TextField
                          size="small"
                          fullWidth
                          disabled={!mapas[item.indice]}
                          value={pl?.precoVarejoStr ?? ''}
                          onChange={(e) => {
                            const formatted = formatCurrencyInputBR(e.target.value);
                            setPrecosLinha((prev) => ({
                              ...prev,
                              [item.indice]: {
                                ...(prev[item.indice] || { custoAtual: 0, varejoAtual: 0, variacaoCusto: 0 }),
                                precoVarejoStr: formatted,
                              },
                            }));
                          }}
                          placeholder="0,00"
                          helperText={margem != null ? `Margem ${margem.toFixed(1)}%` : ' '}
                          FormHelperTextProps={{ sx: { m: 0 } }}
                        />
                      </TableCell>
                      <TableCell data-label="Produto">
                        <Stack direction="row" spacing={0.5} alignItems="flex-start">
                          <Autocomplete
                            size="small"
                            sx={{ flex: 1, minWidth: 180 }}
                            options={productOptions[item.indice] || (mapas[item.indice] ? [mapas[item.indice]] : [])}
                            value={mapas[item.indice] || null}
                            loading={!!searching[item.indice]}
                            onChange={(_e, value) => aoSelecionarProduto(item.indice, value, custoNovo)}
                            onInputChange={(_e, value, reason) => {
                              if (reason === 'input') buscarProdutos(item.indice, value);
                            }}
                            getOptionLabel={(o) => o?.label || ''}
                            isOptionEqualToValue={(a, b) => a?.id === b?.id}
                            noOptionsText="Nenhum produto — use Novo"
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder={item.produtoIdSugerido ? 'Sugerido' : 'Buscar produto...'}
                                error={!mapas[item.indice]}
                              />
                            )}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddIcon />}
                            sx={{ whiteSpace: 'nowrap', mt: 0.25 }}
                            onClick={() => setNovoProdutoDlg({ open: true, item })}
                          >
                            Novo
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </ResponsiveTable>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<LocalOfferIcon />}
              disabled={pendentes > 0 || preview.jaImportada}
              onClick={() => irEtiquetas(produtosParaEtiquetas(preview.itens, mapas, precosLinha))}
            >
              Imprimir etiquetas
            </Button>
            <Button
              variant="contained"
              startIcon={<CheckCircleOutlineIcon />}
              disabled={confirming || preview.jaImportada || pendentes > 0}
              onClick={confirmar}
            >
              {confirming ? 'Importando...' : 'Confirmar entrada de estoque'}
            </Button>
          </Stack>
        </>
      )}

      <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>NF-e importadas</Typography>
      <ResponsiveTable>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>NF</TableCell>
              <TableCell>Emitente</TableCell>
              <TableCell>Chave</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Importada em</TableCell>
              <TableCell align="right">Ação</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {impLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center"><CircularProgress size={24} /></TableCell>
              </TableRow>
            ) : importadas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>Nenhuma NF-e importada</TableCell>
              </TableRow>
            ) : importadas.map((item) => (
              <TableRow key={item.id} hover>
                <TableCell data-label="NF">{item.numero}/{item.serie}</TableCell>
                <TableCell data-label="Emitente">{item.nomeEmitente}</TableCell>
                <TableCell data-label="Chave">
                  <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{item.chave}</Typography>
                </TableCell>
                <TableCell data-label="Total">{money(item.valorTotal)}</TableCell>
                <TableCell data-label="Importada">
                  {item.dataImportacao ? new Date(item.dataImportacao).toLocaleString('pt-BR') : '—'}
                </TableCell>
                <TableCell align="right" data-actions="">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap">
                    <Tooltip title="Ver detalhes">
                      <IconButton size="small" onClick={() => abrirDetalhe(item)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Baixar XML">
                      <IconButton size="small" onClick={() => baixarXmlImportado(item)}>
                        <CodeIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {item.devolucaoVendaId ? (
                      <Button
                        size="small"
                        color="warning"
                        variant="outlined"
                        onClick={() => navigate('/fiscal/nfes', {
                          state: {
                            devolver: {
                              vendaId: item.devolucaoVendaId,
                              nfeImportadaId: item.id,
                              chaveReferenciada: item.chave,
                              naturezaOperacao: 'Devolução de compra',
                              finNFe: 4,
                            },
                          },
                        })}
                      >
                        Emitir devolução
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        startIcon={<UndoIcon />}
                        onClick={() => setDevolverDlg({ open: true, item })}
                      >
                        Devolver
                      </Button>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ResponsiveTable>
      <Stack alignItems="center" sx={{ mt: 1 }}>
        <Pagination
          count={impPages}
          page={impPagina}
          onChange={(_e, p) => carregarImportadas(p)}
          color="primary"
        />
      </Stack>

      <Dialog
        open={devolverDlg.open}
        onClose={() => setDevolverDlg({ open: false, item: null })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Preparar devolução</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography>
              NF {devolverDlg.item?.numero}/{devolverDlg.item?.serie} — {devolverDlg.item?.nomeEmitente}
            </Typography>
            <Alert severity="info">
              Cria uma venda com os itens, quantidades e totais do XML, registra a chave como
              documento referenciado (finNFe=4) e dá saída de estoque. A emissão da NF-e fica
              para você revisar em Emitir NF-e.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDevolverDlg({ open: false, item: null })}>Cancelar</Button>
          <Button variant="contained" disabled={devolverSaving} onClick={confirmarDevolucao}>
            {devolverSaving ? 'Preparando...' : 'Preparar devolução'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={detalheDlg.open}
        onClose={() => setDetalheDlg({ open: false, loading: false, data: null })}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {detalheDlg.data
            ? `NF-e ${detalheDlg.data.numero}/${detalheDlg.data.serie} — ${detalheDlg.data.nomeEmitente}`
            : 'Detalhes da NF-e importada'}
        </DialogTitle>
        <DialogContent dividers>
          {detalheDlg.loading ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : detalheDlg.data ? (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={`Chave ${detalheDlg.data.chave}`} />
                <Chip label={detalheDlg.data.cnpjEmitente} />
                <Chip label={money(detalheDlg.data.valorTotal)} color="primary" />
                {detalheDlg.data.dataEmissao && (
                  <Chip label={`Emissão ${new Date(detalheDlg.data.dataEmissao).toLocaleString('pt-BR')}`} />
                )}
                <Chip label={`Importada ${new Date(detalheDlg.data.dataImportacao).toLocaleString('pt-BR')}`} />
                {detalheDlg.data.devolucaoVendaId && (
                  <Chip color="warning" label={`Devolução preparada · Venda #${detalheDlg.data.devolucaoVendaId}`} />
                )}
              </Stack>
              {detalheDlg.data.observacao && (
                <Typography variant="body2" color="text.secondary">
                  {detalheDlg.data.observacao}
                </Typography>
              )}
              <ResponsiveTable>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Produto local</TableCell>
                      <TableCell>XML</TableCell>
                      <TableCell align="right">Qtd</TableCell>
                      <TableCell align="right">Unit.</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detalheDlg.data.itens || []).map((item) => (
                      <TableRow key={item.indice}>
                        <TableCell data-label="#">{item.indice}</TableCell>
                        <TableCell data-label="Produto">
                          <Typography variant="body2" fontWeight={700}>
                            {item.produtoCodigo || item.produtoId}
                            {item.produtoDescricao ? ` · ${item.produtoDescricao}` : ''}
                          </Typography>
                        </TableCell>
                        <TableCell data-label="XML">
                          <Typography variant="body2">{item.codigoXml}</Typography>
                          <Typography variant="caption" color="text.secondary">{item.descricao}</Typography>
                        </TableCell>
                        <TableCell align="right" data-label="Qtd">{item.quantidade}</TableCell>
                        <TableCell align="right" data-label="Unit.">{money(item.valorUnitario)}</TableCell>
                        <TableCell align="right" data-label="Total">{money(item.valorTotal)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ResponsiveTable>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          {detalheDlg.data && (
            <Button
              startIcon={<LocalOfferIcon />}
              onClick={() => {
                const produtos = (detalheDlg.data.itens || [])
                  .filter((i) => i.produtoId)
                  .reduce((acc, i) => {
                    if (acc.some((p) => p.id === i.produtoId)) return acc;
                    acc.push({
                      id: i.produtoId,
                      codigo: i.produtoCodigo || '',
                      descricao: i.produtoDescricao || i.descricao || '',
                      quantidade: 1,
                    });
                    return acc;
                  }, []);
                irEtiquetas(produtos);
              }}
            >
              Etiquetas
            </Button>
          )}
          {detalheDlg.data && (
            <Button
              startIcon={<CodeIcon />}
              onClick={() => baixarXmlImportado(detalheDlg.data)}
            >
              Baixar XML
            </Button>
          )}
          <Button onClick={() => setDetalheDlg({ open: false, loading: false, data: null })}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <NfeXmlNovoProdutoDialog
        open={novoProdutoDlg.open}
        item={novoProdutoDlg.item}
        estabelecimentoId={Number(estabelecimentoId) || 1}
        onClose={() => setNovoProdutoDlg({ open: false, item: null })}
        onCreated={(produto) => {
          const item = novoProdutoDlg.item;
          const indice = item?.indice;
          if (indice == null) return;
          const map = {
            id: produto.id,
            codigo: produto.codigo,
            descricao: produto.descricao,
            label: produto.label || `${produto.codigo || produto.id} · ${produto.descricao || ''}`,
          };
          setMapas((m) => ({ ...m, [indice]: map }));
          setProductOptions((prev) => ({
            ...prev,
            [indice]: [map, ...(prev[indice] || []).filter((p) => p.id !== map.id)],
          }));
          const custoNovo = Number(item?.custoNovo) || Number(item?.valorUnitario) || 0;
          aplicarPrecosLinha(
            indice,
            {
              custoAtual: Number(produto.custo) || custoNovo,
              varejoAtual: Number(produto.varejo) || 0,
              codigo: produto.codigo,
            },
            custoNovo,
          );
          if (produto.varejo != null) {
            setPrecosLinha((prev) => ({
              ...prev,
              [indice]: {
                ...(prev[indice] || {}),
                precoVarejoStr: formatBRLFromNumber(Number(produto.varejo) || 0),
              },
            }));
          }
          setSuccess(`Produto cadastrado e vinculado ao item ${indice}.`);
        }}
      />
    </Box>
  );
}
